/**
 * LoopNest Generation Pipeline Service
 * Keyframe-Based Architecture — Low-cost scalable: ~$0.01–$0.02 per finished video
 *
 * Architecture:
 * 1. Analyze music (Google Gemini Flash) → extract BPM, mood, genre, scene themes
 * 2. Scene segmentation — split script into max 5–8 scenes per video
 * 3. Keyframe generation — ONE AI image per scene via Stable Diffusion (max 8 total)
 * 4. Scene animation — animate each keyframe with motion transforms:
 *      pan, zoom, depth motion, frame interpolation
 * 5. Frame interpolation — smooth motion between keyframes (no new AI frames)
 * 6. Video assembly — combine animated scenes into final timeline
 * 7. Audio attach — attach generated or uploaded music to final render
 *
 * Cost targets (keyframe model):
 * - Music analysis (Gemini Flash) ≈ $0.0001 per analysis
 * - Keyframe image generation ≈ $0.002 per keyframe × max 8 = $0.016 max
 * - Frame interpolation ≈ $0.0005 per scene (no new AI frames)
 * - Video assembly ≈ $0.002
 * - Total ≈ $0.01–$0.02 per video (50–75% cheaper than full-frame rendering)
 *
 * KEY CONSTRAINT: max_keyframes_per_video = 8
 * All videos are rendered from keyframes, NOT frame-by-frame AI generation.
 */

import { supabase } from '../lib/supabase';
import { isPlatformOwnerEmail } from '../utils/adminOverride';
import {
  checkVideoSecondsQuota as checkVideoSecondsQuotaV2,
  deductVideoSeconds as deductVideoSecondsV2,
  refundVideoSeconds as refundVideoSecondsV2,
  checkMusicQuota,
  deductMusicGeneration,
  refundMusicGeneration,
  checkGameQuota,
  deductGameGeneration,
  refundGameGeneration,
  getComputeWalletStatus,
  COMPUTE_PLANS,
} from './computeWallet';

// ─── KEYFRAME ARCHITECTURE CONSTANTS ─────────────────────────────────────────
// CRITICAL: All videos MUST be rendered from keyframes only.
// Frame-by-frame AI generation is DISABLED to keep GPU costs extremely low.

export const KEYFRAME_CONFIG = {
  /** Hard limit: maximum AI-generated images per video */
  max_keyframes_per_video: 8,
  /** Minimum scenes for any video */
  min_scenes: 5,
  /** Default scenes when duration is unknown */
  default_scenes: 6,
  /** Seconds of video output per keyframe (via interpolation) */
  seconds_per_keyframe: 7.5,  // 8 keyframes × 7.5s = 60s video
  /** Motion transform types applied to each keyframe */
  motion_transforms: ['pan', 'zoom', 'depth_motion'],
  /** Frame interpolation FPS target */
  interpolation_fps: 24,
  /** Interpolation frames generated between keyframes (no AI cost) */
  interpolation_frames_per_scene: 180, // 7.5s × 24fps
};

// ─── QUEUE STATE CONSTANTS ────────────────────────────────────────────────────
// All generation must pass through the queue. Direct engine calls are not allowed.
// Valid queue states: pending → processing → completed | failed

export const QUEUE_STATES = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

// ─── COIN COSTS ──────────────────────────────────────────────────────────────
// Updated for keyframe model: costs reflect keyframe generation only

export const COIN_COSTS = {
  image_generation: 1,      // 1 coin per keyframe image (~$0.002)
  animation_clip: 1,        // 1 coin per animated keyframe scene (interpolation only, ~$0.001)
  full_music_video: 10,     // 10 coins for full music video (8 keyframes + assembly)
  scene_batch: 5,           // 5 coins for a batch of up to 8 keyframe scenes
};

export const COIN_COST_LABELS = {
  image_generation: '1 coin',
  animation_clip: '1 coin',
  full_music_video: '10 coins',
  scene_batch: '5 coins',
};

// ─── VIDEO SECONDS QUOTA SYSTEM ──────────────────────────────────────────────
// Primary billing metric: 1 generated second = 1 credit
// 30s video = 30 credits, 60s video = 60 credits, 5min video = 300 credits

export const VIDEO_SECONDS_PLANS = {
  free: {
    monthlySeconds: 120,      // 120 video seconds per month
    maxVideoSeconds: 30,      // max 30s per video
    watermark: true,
    hdExport: false,
    priority: false,
    label: 'Free',
    price: '$0',
    resetInterval: 'daily',   // free resets daily
  },
  creator: {
    monthlySeconds: 1800,
    maxVideoSeconds: 60,
    watermark: false,
    hdExport: true,
    priority: false,
    label: 'Creator',
    price: '$12/month',
    resetInterval: 'monthly',
  },
  pro: {
    monthlySeconds: 1800,     // 1800 video seconds per month
    maxVideoSeconds: 60,      // max 60s per video
    watermark: false,
    hdExport: true,
    priority: false,
    label: 'Creator',
    price: '$12/month',
    resetInterval: 'monthly',
  },
  studio: {
    monthlySeconds: 7200,     // 7200 video seconds per month
    maxVideoSeconds: 180,     // max 3 minutes per video
    watermark: false,
    hdExport: true,
    priority: true,
    label: 'Studio',
    price: '$29/month',
    resetInterval: 'monthly',
  },
  pro_creator: {
    monthlySeconds: 20000,
    maxVideoSeconds: 300,
    watermark: false,
    hdExport: true,
    priority: true,
    label: 'Pro Creator',
    price: '$59/month',
    resetInterval: 'monthly',
  },
  enterprise: {
    monthlySeconds: 20000,    // 20000 video seconds per month
    maxVideoSeconds: 300,     // max 5 minutes per video
    watermark: false,
    hdExport: true,
    priority: true,
    label: 'Pro Creator',
    price: '$59/month',
    resetInterval: 'monthly',
  },
};

// ─── FREE PLAN LIMITS (legacy compat) ────────────────────────────────────────

export const FREE_PLAN_LIMITS = {
  videos_per_day: 4,          // ~120s / 30s per video
  max_duration_seconds: 30,
  watermark: true,
  hd_export: false,
  priority_rendering: false,
};

export const TIER_LIMITS = {
  free: {
    videos_per_day: 4,          // ~120s / 30s per video
    max_duration_seconds: 30,
    watermark: true,
    hd_export: false,
    priority_rendering: false,
    starting_coins: 50,
    monthly_video_seconds: 120,
  },
  pro: {
    videos_per_day: 30,         // ~1800s / 60s per video
    max_duration_seconds: 60,
    watermark: false,
    hd_export: true,
    priority_rendering: false,
    starting_coins: 200,
    monthly_video_seconds: 1800,
  },
  studio: {
    videos_per_day: 40,         // ~7200s / 180s per video
    max_duration_seconds: 180,
    watermark: false,
    hd_export: true,
    priority_rendering: true,
    starting_coins: 500,
    monthly_video_seconds: 7200,
  },
  enterprise: {
    videos_per_day: 999999,
    max_duration_seconds: 300,
    watermark: false,
    hd_export: true,
    priority_rendering: true,
    starting_coins: 9999,
    monthly_video_seconds: 20000,
  },
};

// ─── REPLICATE MODELS ────────────────────────────────────────────────────────
// These are the low-cost open model endpoints

export const REPLICATE_MODELS = {
  // Stable Diffusion XL — ~$0.002 per keyframe image
  stable_diffusion_xl: 'stability-ai/sdxl:39ed52f2319f9b2a9f3f8e7a2e3a3b3c3d3e3f3a',
  // Stable Video Diffusion — ~$0.01 per clip (NOT used in keyframe mode)
  stable_video_diffusion: 'stability-ai/stable-video-diffusion:3f0457e4619daac51203dedb472816fd4af51f3149fa7a9e0b5ffcf1b8172438',
  // AnimateDiff — alternative animation model (NOT used in keyframe mode)
  animate_diff: 'lucataco/animate-diff:beecf59c4aee8d81bf04f0381033dfa10dc16e845b4ae00d281e2fa377e48a9f',
  // SDXL Lightning — faster/cheaper variant for keyframe generation
  sdxl_lightning: 'bytedance/sdxl-lightning-4step:5f24084160c9089501c1b3545d9be3c27883ae2239b6f412990e82d4a6210f8f',
};

// ─── SCENE PROMPT TEMPLATES ──────────────────────────────────────────────────

const SCENE_PROMPT_TEMPLATES = {
  nature: [
    'Cinematic establishing shot of a vast forest landscape at golden hour, 8K, photorealistic',
    'Slow motion clouds drifting across a dramatic sky, cinematic, high detail',
    'Sunlight filtering through ancient trees, volumetric lighting, photorealistic',
    'Crystal clear river flowing over smooth rocks, aerial drone shot, cinematic',
    'Wide aerial shot of mountain range at sunrise, epic scale, photorealistic',
    'Close-up of morning dew on green leaves, macro photography, cinematic',
    'Misty valley at dawn with golden light rays, atmospheric, photorealistic',
    'Ocean waves at sunset, long exposure, cinematic, 8K',
  ],
  urban: [
    'Cinematic city skyline at night with neon reflections, 8K, photorealistic',
    'Busy street with motion blur, long exposure photography, cinematic',
    'Modern architecture with dramatic lighting, architectural photography',
    'Aerial drone shot of city grid at dusk, golden hour, photorealistic',
    'Underground subway station with light trails, cinematic, high detail',
    'Rooftop view of city with fog and bokeh lights, cinematic',
    'Rain-soaked street with neon reflections, cinematic noir, photorealistic',
    'Bridge at night with light trails, long exposure, cinematic',
  ],
  cinematic: [
    'Epic cinematic landscape with dramatic storm clouds, 8K, photorealistic',
    'Golden hour silhouette against vast sky, cinematic composition',
    'Misty forest with shafts of light, atmospheric, photorealistic',
    'Dramatic ocean waves crashing on cliffs, slow motion, cinematic',
    'Desert dunes at sunset with long shadows, aerial, photorealistic',
    'Mountain peak above clouds, epic vista, cinematic, 8K',
    'Volcanic landscape at dusk, dramatic lighting, photorealistic',
    'Frozen tundra under aurora borealis, cinematic, 8K',
  ],
  worship: [
    'Rays of light breaking through clouds over a peaceful valley, spiritual, cinematic',
    'Sunrise over calm water with golden reflections, peaceful, photorealistic',
    'Star-filled night sky over a mountain, Milky Way visible, 8K',
    'Soft morning light through stained glass, warm colors, cinematic',
    'Candles glowing in a dark space, warm bokeh, cinematic',
    'Hands raised in worship silhouetted against sunset, cinematic',
    'Peaceful chapel in a forest clearing, golden light, photorealistic',
    'Dove in flight against a bright sky, symbolic, cinematic',
  ],
  documentary: [
    'Authentic candid portrait of a person in natural light, documentary style',
    'Real landscape with natural lighting, documentary photography',
    'Community gathering in an open space, candid, photorealistic',
    'Close-up portrait with shallow depth of field, documentary',
    'Street scene with authentic people, documentary photography',
    'Market scene with vibrant colors, candid, photorealistic',
    'Children playing outdoors, natural light, documentary',
    'Elder craftsman at work, close-up, documentary style',
  ],
};

// ─── SCENE SEGMENTATION ───────────────────────────────────────────────────────

/**
 * Segment a video into scenes based on duration and BPM.
 * Returns between min_scenes and max_keyframes_per_video scenes.
 * Each scene maps to exactly ONE keyframe image.
 *
 * @param {number} durationSeconds - total video duration
 * @param {number} bpm - beats per minute (affects scene pacing)
 * @returns {{ sceneCount: number, sceneDurations: number[], totalSeconds: number }}
 */
export function segmentVideoIntoScenes(durationSeconds, bpm = 120) {
  const { max_keyframes_per_video, min_scenes, seconds_per_keyframe } = KEYFRAME_CONFIG;

  // Calculate natural scene count from duration
  const naturalSceneCount = Math.ceil(durationSeconds / seconds_per_keyframe);

  // Clamp to [min_scenes, max_keyframes_per_video]
  const sceneCount = Math.min(
    max_keyframes_per_video,
    Math.max(min_scenes, naturalSceneCount)
  );

  // Distribute duration evenly across scenes
  const baseDuration = Math.floor(durationSeconds / sceneCount);
  const remainder = durationSeconds - baseDuration * sceneCount;

  const sceneDurations = Array.from({ length: sceneCount }, (_, i) =>
    i === sceneCount - 1 ? baseDuration + remainder : baseDuration
  );

  return {
    sceneCount,
    sceneDurations,
    totalSeconds: durationSeconds,
    keyframesRequired: sceneCount, // 1 keyframe per scene
  };
}

// ─── MOTION TRANSFORM ASSIGNMENT ─────────────────────────────────────────────

/**
 * Assign a motion transform to each keyframe scene.
 * Transforms are applied during canvas animation — no new AI frames generated.
 *
 * Available transforms:
 *   pan       — horizontal/vertical camera pan across the keyframe
 *   zoom      — slow zoom in or out (Ken Burns effect)
 *   depth_motion — parallax depth simulation
 *
 * @param {number} sceneCount
 * @param {string} mood - affects transform selection
 * @returns {Array<{ transform: string, direction: string, intensity: number }>}
 */
export function assignMotionTransforms(sceneCount, mood = '') {
  const moodLower = mood?.toLowerCase() || '';

  // Mood-based transform preferences
  const moodTransformMap = {
    energetic: ['zoom', 'pan', 'zoom', 'pan', 'depth_motion', 'zoom', 'pan', 'zoom'],
    calm: ['zoom', 'depth_motion', 'zoom', 'pan', 'depth_motion', 'zoom', 'depth_motion', 'pan'],
    dramatic: ['depth_motion', 'zoom', 'pan', 'depth_motion', 'zoom', 'pan', 'depth_motion', 'zoom'],
    uplifting: ['zoom', 'pan', 'depth_motion', 'zoom', 'pan', 'zoom', 'pan', 'depth_motion'],
    melancholic: ['pan', 'depth_motion', 'pan', 'zoom', 'depth_motion', 'pan', 'zoom', 'depth_motion'],
  };

  const defaultTransforms = ['zoom', 'pan', 'depth_motion', 'zoom', 'pan', 'depth_motion', 'zoom', 'pan'];

  let baseTransforms = defaultTransforms;
  for (const [key, transforms] of Object.entries(moodTransformMap)) {
    if (moodLower?.includes(key)) {
      baseTransforms = transforms;
      break;
    }
  }

  const directions = ['left', 'right', 'up', 'down', 'in', 'out'];

  return Array.from({ length: sceneCount }, (_, i) => ({
    transform: baseTransforms?.[i % baseTransforms?.length],
    direction: directions?.[i % directions?.length],
    intensity: 0.3 + (i % 3) * 0.1, // 0.3 – 0.5 subtle motion
    interpolationFps: KEYFRAME_CONFIG?.interpolation_fps,
    interpolationFrames: KEYFRAME_CONFIG?.interpolation_frames_per_scene,
  }));
}

// ─── MUSIC ANALYSIS VIA GOOGLE GEMINI FLASH ──────────────────────────────────

/**
 * Analyze audio metadata using Google Gemini Flash to extract scene themes.
 * Generates exactly max_keyframes_per_video (8) scene prompts — one per keyframe.
 * Uses the Lambda-based Gemini integration for low-cost AI reasoning.
 * Model: gemini/gemini-2.5-flash — optimized for speed and cost.
 */
export async function analyzeMusicForScenes(params) {
  const { title = '', genre = '', mood = '', prompt = '', duration = 60, bpm = 120 } = params;

  // Determine scene count based on duration
  const { sceneCount } = segmentVideoIntoScenes(duration, bpm);

  try {
    const lambdaUrl = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;
    if (!lambdaUrl) {
      return generateFallbackScenePrompts(params);
    }

    const systemPrompt = `You are a music video director using a keyframe-based rendering pipeline. Given song metadata, generate exactly ${sceneCount} cinematic keyframe scene descriptions. Each scene maps to ONE AI-generated image that will be animated with motion transforms (pan, zoom, depth). Each prompt must be a detailed, photorealistic Stable Diffusion prompt with: scene description, lighting style, camera angle, environment, and character activity. Return ONLY a JSON array of ${sceneCount} strings, no other text.`;

    const userPrompt = `Song: "${title}"
Genre: ${genre || 'unknown'}
Mood: ${mood || 'unknown'}
BPM: ${bpm}
Duration: ${duration}s
Theme: ${prompt || 'cinematic music video'}
Scenes needed: ${sceneCount} (keyframe-based pipeline, 1 AI image per scene)

Generate ${sceneCount} keyframe scene prompts. Each keyframe will be animated with pan/zoom/depth motion transforms — no additional AI frames will be generated. Each prompt must include: scene description, lighting style (e.g. golden hour, neon, soft diffused), camera angle (e.g. wide establishing shot, close-up, aerial drone), environment (e.g. forest, city rooftop, desert), and character activity (e.g. silhouette walking, hands raised, crowd dancing). Make each prompt detailed and photorealistic for Stable Diffusion XL. Return as JSON array only.`;

    const response = await fetch(lambdaUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider: 'GEMINI',
        model: 'gemini/gemini-2.5-flash',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        parameters: {
          temperature: 0.7,
          max_tokens: 800,  // Limit tokens for cost optimization
        },
      }),
    });

    if (!response?.ok) throw new Error('Gemini analysis failed');

    const data = await response?.json();
    const content = data?.choices?.[0]?.message?.content || '';

    // Parse JSON array from response
    const jsonMatch = content?.match(/\[.*\]/s);
    if (jsonMatch) {
      const scenes = JSON.parse(jsonMatch?.[0]);
      if (Array.isArray(scenes) && scenes?.length > 0) {
        // Enforce max_keyframes_per_video hard limit
        const capped = scenes?.slice(0, KEYFRAME_CONFIG?.max_keyframes_per_video);
        return { scenes: capped, source: 'gemini', sceneCount: capped?.length };
      }
    }

    return generateFallbackScenePrompts(params);
  } catch (err) {
    console.warn('[GenerationPipeline] Gemini analysis failed, using fallback:', err?.message);
    return generateFallbackScenePrompts(params);
  }
}

/**
 * Generate scene prompts without AI (fallback)
 * Always respects max_keyframes_per_video limit.
 */
function generateFallbackScenePrompts(params) {
  const { styleId = 'cinematic', bpm = 120, duration = 60 } = params;
  const styleKey = styleId?.replace('-stock', '')?.replace('-photos', '')?.replace('realistic-', '');
  const templates = SCENE_PROMPT_TEMPLATES?.[styleKey] || SCENE_PROMPT_TEMPLATES?.cinematic;

  // Use scene segmentation to determine count — capped at max_keyframes_per_video
  const { sceneCount } = segmentVideoIntoScenes(duration, bpm);
  const scenes = [];
  for (let i = 0; i < sceneCount; i++) {
    scenes?.push(templates?.[i % templates?.length]);
  }

  return { scenes, source: 'fallback', sceneCount: scenes?.length };
}

// ─── KEYFRAME GENERATION PIPELINE ────────────────────────────────────────────

/**
 * Generate all keyframes for a video.
 * Enforces max_keyframes_per_video = 8 hard limit.
 * Each keyframe is ONE Stable Diffusion image — no full-frame AI rendering.
 *
 * @param {string[]} scenePrompts - array of scene prompts (max 8)
 * @param {string} styleId
 * @param {function} onKeyframeProgress - callback(index, total)
 * @returns {Promise<Array<{ imageUrl, source, prompt, index, motionTransform }>>}
 */
export async function generateKeyframes(scenePrompts, styleId = 'cinematic', onKeyframeProgress = null) {
  // Enforce hard limit
  const cappedPrompts = scenePrompts?.slice(0, KEYFRAME_CONFIG?.max_keyframes_per_video);
  const total = cappedPrompts?.length;

  console.log(`[KeyframePipeline] Generating ${total} keyframes (max: ${KEYFRAME_CONFIG?.max_keyframes_per_video})`);

  const results = await Promise.allSettled(
    cappedPrompts?.map(async (prompt, i) => {
      const image = await generateSceneImage(prompt, { seed: i + 1, styleId });
      onKeyframeProgress?.(i + 1, total);
      return { ...image, index: i };
    })
  );

  return results?.map((r, i) =>
    r?.status === 'fulfilled'
      ? r?.value
      : { imageUrl: null, source: 'failed', prompt: cappedPrompts?.[i], index: i }
  );
}

/**
 * Build the complete keyframe scene manifest.
 * Combines keyframe images with motion transforms and interpolation config.
 * This manifest drives the canvas animation engine — no additional AI calls.
 *
 * @param {Array} keyframes - output from generateKeyframes()
 * @param {number[]} sceneDurations - duration per scene in seconds
 * @param {string} mood
 * @returns {Array<KeyframeScene>}
 */
export function buildKeyframeSceneManifest(keyframes, sceneDurations, mood = '') {
  const motionTransforms = assignMotionTransforms(keyframes?.length, mood);

  return keyframes?.map((kf, i) => ({
    index: i,
    imageUrl: kf?.imageUrl,
    source: kf?.source,
    prompt: kf?.prompt,
    durationSeconds: sceneDurations?.[i] ?? KEYFRAME_CONFIG?.seconds_per_keyframe,
    motionTransform: motionTransforms?.[i],
    interpolation: {
      fps: KEYFRAME_CONFIG?.interpolation_fps,
      frames: Math.round((sceneDurations?.[i] ?? KEYFRAME_CONFIG?.seconds_per_keyframe) * KEYFRAME_CONFIG?.interpolation_fps),
      method: 'linear', // frame interpolation — no new AI frames
    },
  }));
}

// ─── GPU JOB QUEUE ───────────────────────────────────────────────────────────

/**
 * Queue a generation job in Supabase
 * Returns the job ID for polling
 */
export async function queueGenerationJob(params) {
  const {
    userId,
    jobType = 'full_music_video',
    prompt,
    styleId,
    durationSeconds,
    bpm,
    mood,
    genre,
    videoTitle,
    audioUrl,
    scenePrompts = [],
    keyframeCount = 0,
  } = params;

  const coinsRequired = COIN_COSTS?.[jobType] || COIN_COSTS?.full_music_video;

  const { data, error } = await supabase?.from('generation_jobs')?.insert({
      user_id: userId,
      job_type: jobType,
      status: 'queued',
      prompt,
      style_id: styleId,
      duration_seconds: durationSeconds,
      bpm,
      mood,
      genre,
      video_title: videoTitle,
      audio_url: audioUrl,
      scene_prompts: scenePrompts,
      coins_charged: coinsRequired,
      progress_percent: 0,
      current_stage: QUEUE_STATES?.PENDING,
      // Keyframe metadata
      metadata: {
        keyframe_mode: true,
        keyframe_count: keyframeCount || Math.min(scenePrompts?.length, KEYFRAME_CONFIG?.max_keyframes_per_video),
        max_keyframes: KEYFRAME_CONFIG?.max_keyframes_per_video,
        rendering_mode: 'keyframe_interpolation',
      },
    })?.select()?.single();

  if (error) throw new Error(`Failed to queue job: ${error.message}`);
  return data;
}

/**
 * Update job progress
 */
export async function updateJobProgress(jobId, updates) {
  const { error } = await supabase?.from('generation_jobs')?.update(updates)?.eq('id', jobId);

  if (error) console.error('[GenerationPipeline] updateJobProgress error:', error);
}

/**
 * Get job status
 */
export async function getJobStatus(jobId) {
  const { data, error } = await supabase?.from('generation_jobs')?.select('*')?.eq('id', jobId)?.single();

  if (error) return null;
  return data;
}

/**
 * Get user's generation jobs
 */
export async function getUserJobs(userId, limit = 20) {
  const { data, error } = await supabase?.from('generation_jobs')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

  if (error) return [];
  return data || [];
}

// ─── COIN DEDUCTION ──────────────────────────────────────────────────────────

/**
 * Deduct coins for a generation job
 * Uses atomic DB function to prevent race conditions
 */
export async function deductCoinsForGeneration(userId, jobType, jobId = null) {
  const amount = COIN_COSTS?.[jobType] || COIN_COSTS?.full_music_video;
  const reason = `Generation: ${jobType?.replace(/_/g, ' ')}`;

  const { data, error } = await supabase?.rpc('deduct_coins', {
    p_user_id: userId,
    p_amount: amount,
    p_reason: reason,
    p_job_id: jobId,
  });

  if (error) return { success: false, error: error?.message };
  return data;
}

/**
 * Refund coins if generation fails
 */
export async function refundCoinsForFailedJob(userId, jobType, jobId = null) {
  const amount = COIN_COSTS?.[jobType] || COIN_COSTS?.full_music_video;
  const reason = `Refund: failed generation job`;

  const { data, error } = await supabase?.rpc('grant_coins', {
    p_user_id: userId,
    p_amount: amount,
    p_reason: reason,
    p_job_id: jobId,
  });

  if (error) return { success: false, error: error?.message };
  return data;
}

/**
 * Check if user has enough coins
 */
export async function checkCoinBalance(userId, jobType) {
  const required = COIN_COSTS?.[jobType] || COIN_COSTS?.full_music_video;

  const { data, error } = await supabase?.from('coin_balances')?.select('balance')?.eq('user_id', userId)?.single();

  if (error) return { sufficient: false, balance: 0, required };

  const balance = data?.balance ?? 0;
  return { sufficient: balance >= required, balance, required };
}

// ─── FREE PLAN ENFORCEMENT ───────────────────────────────────────────────────

/**
 * Check if user can generate a video today
 * Returns { allowed, reason, limit, count }
 */
export async function checkGenerationAllowed(userId, userTier = 'free', userEmail = '') {
  // Platform owner always allowed
  if (isPlatformOwnerEmail(userEmail)) {
    return { allowed: true, reason: 'Platform owner override', limit: 999999, count: 0 };
  }

  const { data, error } = await supabase?.rpc('check_daily_video_limit', {
    p_user_id: userId,
    p_tier: userTier,
  });

  if (error) {
    console.error('[GenerationPipeline] checkGenerationAllowed error:', error);
    return { allowed: false, reason: 'Could not verify generation limit', limit: 1, count: 0 };
  }

  return {
    allowed: data?.allowed ?? false,
    reason: data?.error || 'OK',
    limit: data?.limit ?? 1,
    count: data?.count ?? 0,
  };
}

/**
 * Get tier limits for a user
 */
export function getTierLimits(tier = 'free') {
  return TIER_LIMITS?.[tier] || TIER_LIMITS?.free;
}

/**
 * Check if free user should get watermark
 */
export function shouldWatermark(tier = 'free', userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) return false;
  return TIER_LIMITS?.[tier]?.watermark ?? true;
}

// ─── REPLICATE IMAGE GENERATION ──────────────────────────────────────────────

/**
 * Generate a single keyframe image using Stable Diffusion via Replicate.
 * Cost: ~$0.002 per keyframe image.
 * Called max 8 times per video (max_keyframes_per_video).
 *
 * NOTE: In production, this should be called from a backend/edge function
 * to keep the Replicate API key secure. The frontend queues the job and
 * polls for completion.
 *
 * For the current architecture (client-side), we use the Supabase Edge Function
 * as a proxy. If no edge function is available, we fall back to Unsplash.
 */
export async function generateSceneImage(prompt, options = {}) {
  const { width = 1280, height = 720, seed = 0, styleId = 'cinematic' } = options;

  try {
    // Try Supabase Edge Function proxy for Replicate
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');

    const { data, error } = await supabase?.functions?.invoke('generate-scene-image', {
      body: { prompt, width, height, seed, styleId },
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });

    if (error || !data?.imageUrl) throw new Error(error?.message || 'No image returned');

    return { imageUrl: data?.imageUrl, source: 'replicate', prompt };
  } catch (err) {
    // Fallback to Unsplash (free, no API key needed)
    const styleQueries = {
      nature: 'nature landscape cinematic',
      urban: 'city urban cinematic',
      cinematic: 'cinematic dramatic landscape',
      worship: 'spiritual light peaceful',
      documentary: 'documentary authentic',
    };
    const styleKey = styleId?.replace('-stock', '')?.replace('-photos', '')?.replace('realistic-', '');
    let query = styleQueries?.[styleKey] || 'cinematic landscape';
    const imageUrl = `https://source.unsplash.com/${width}x${height}/?${encodeURIComponent(query)}&sig=${seed}`;
    return { imageUrl, source: 'unsplash_fallback', prompt };
  }
}

/**
 * Generate multiple keyframe images in parallel (batch).
 * Hard-capped at max_keyframes_per_video = 8.
 * Returns array of { imageUrl, source, prompt }
 */
export async function generateSceneImageBatch(prompts, styleId = 'cinematic') {
  // Enforce hard limit before any API calls
  const cappedPrompts = prompts?.slice(0, KEYFRAME_CONFIG?.max_keyframes_per_video);

  const results = await Promise.allSettled(
    cappedPrompts?.map((prompt, i) =>
      generateSceneImage(prompt, { seed: i + 1, styleId })
    )
  );

  return results?.map((r, i) =>
    r?.status === 'fulfilled'
      ? r?.value
      : { imageUrl: null, source: 'failed', prompt: cappedPrompts?.[i] }
  );
}

// ─── ASSET STORAGE ───────────────────────────────────────────────────────────

/**
 * Upload a generated asset to Supabase Storage
 */
export async function uploadGeneratedAsset(params) {
  const { userId, jobId, assetType, blob, fileName, mimeType } = params;

  const bucketMap = {
    image: 'generated-images',
    video: 'rendered-videos',
    audio: 'user-audio',
    thumbnail: 'generated-images',
  };

  const bucket = bucketMap?.[assetType] || 'generated-images';
  const storagePath = `${userId}/${jobId || 'standalone'}/${fileName}`;

  const { data, error } = await supabase?.storage?.from(bucket)?.upload(storagePath, blob, {
      contentType: mimeType,
      upsert: true,
    });

  if (error) throw new Error(`Storage upload failed: ${error.message}`);

  const { data: urlData } = supabase?.storage?.from(bucket)?.getPublicUrl(storagePath);

  const publicUrl = urlData?.publicUrl;

  // Record in generated_assets table
  await supabase?.from('generated_assets')?.insert({
    user_id: userId,
    job_id: jobId,
    asset_type: assetType,
    bucket_name: bucket,
    storage_path: storagePath,
    public_url: publicUrl,
    mime_type: mimeType,
    file_size_bytes: blob?.size,
  });

  return { storagePath, publicUrl, bucket };
}

/**
 * Get all generated assets for a user
 */
export async function getUserGeneratedAssets(userId, assetType = null) {
  let query = supabase?.from('generated_assets')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

  if (assetType) query = query?.eq('asset_type', assetType);

  const { data, error } = await query?.limit(50);
  if (error) return [];
  return data || [];
}

// ─── VIDEO SECONDS QUOTA FUNCTIONS ───────────────────────────────────────────

/**
 * Check if user has enough video seconds quota for the requested duration.
 * Also validates that requested duration does not exceed plan's max video length.
 * Platform owner (365ddevotional@gmail.com) always bypasses all checks.
 */
export async function checkVideoSecondsQuota(userId, userTier = 'free', requestedSeconds = 30, userEmail = '') {
  return checkVideoSecondsQuotaV2(userId, userTier, requestedSeconds, userEmail);
}

/**
 * Atomically deduct video seconds from user quota.
 */
export async function deductVideoSeconds(userId, userTier = 'free', requestedSeconds = 30, jobId = null, userEmail = '') {
  return deductVideoSecondsV2(userId, userTier, requestedSeconds, jobId, userEmail);
}

/**
 * Refund video seconds on failed generation.
 */
export async function refundVideoSeconds(userId, seconds, jobId = null, userEmail = '') {
  return refundVideoSecondsV2(userId, seconds, jobId, userEmail);
}

/**
 * Get current quota status for a user (video seconds only, for backward compat).
 */
export async function getUserQuotaStatus(userId, userTier = 'free', userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return {
      used: 0,
      quota: 20000,
      remaining: 20000,
      maxVideoSeconds: 300,
      percentUsed: 0,
      watermark: false,
      hdExport: true,
      priority: true,
      isPlatformOwner: true,
    };
  }

  try {
    const wallet = await getComputeWalletStatus(userId, userTier, userEmail);
    const used = wallet?.videoSecondsUsed ?? 0;
    const quota = wallet?.videoSecondsQuota ?? 120;
    return {
      used,
      quota,
      remaining: wallet?.videoSecondsRemaining ?? 0,
      maxVideoSeconds: wallet?.maxVideoSeconds ?? 30,
      percentUsed: quota > 0 ? Math.round((used / quota) * 100) : 0,
      watermark: wallet?.watermark ?? true,
      hdExport: wallet?.hdExport ?? false,
      priority: wallet?.priority ?? false,
    };
  } catch (err) {
    console.error('[GenerationPipeline] getUserQuotaStatus error:', err?.message);
    const plan = VIDEO_SECONDS_PLANS?.[userTier] || VIDEO_SECONDS_PLANS?.free;
    return {
      used: 0,
      quota: plan?.monthlySeconds,
      remaining: plan?.monthlySeconds,
      maxVideoSeconds: plan?.maxVideoSeconds,
      percentUsed: 0,
      watermark: plan?.watermark,
      hdExport: plan?.hdExport,
      priority: plan?.priority,
    };
  }
}

/**
 * Get video seconds usage history for a user.
 */
export async function getVideoSecondsHistory(userId, limit = 30) {
  const { data, error } = await supabase?.from('video_seconds_usage')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

  if (error) return [];
  return (data || [])?.map((r) => ({
    id: r?.id,
    jobId: r?.job_id,
    secondsUsed: r?.seconds_used,
    requestedSeconds: r?.requested_seconds,
    tierAtTime: r?.tier_at_time,
    wasBlocked: r?.was_blocked,
    blockReason: r?.block_reason,
    createdAt: r?.created_at,
  }));
}

// ─── FULL PIPELINE ORCHESTRATOR ──────────────────────────────────────────────

/**
 * Run the complete keyframe-based music video generation pipeline.
 *
 * Keyframe Architecture Steps:
 * 1. Check daily limit
 * 2. Check coin balance
 * 3. Scene segmentation — split video into max 5–8 scenes
 * 4. Queue job with keyframe metadata
 * 5. Deduct coins
 * 6. Analyze music → generate scene prompts (Gemini Flash)
 * 7. Generate keyframes — ONE AI image per scene (max 8 total, Stable Diffusion)
 * 8. Assign motion transforms — pan/zoom/depth per keyframe
 * 9. Build keyframe scene manifest with interpolation config
 * 10. Assemble final video from keyframes + interpolation (no new AI frames)
 * 11. Attach audio
 * 12. Store assets and mark job complete
 *
 * GPU cost is based on keyframe count only (max 8 images × $0.002 = $0.016 max).
 *
 * @param {object} params
 * @param {function} onProgress - callback(stage, percent, detail)
 * @returns {object} { jobId, videoUrl, thumbnailUrl, sceneImages, keyframeManifest }
 */
export async function runGenerationPipeline(params, onProgress) {
  const {
    userId,
    userEmail = '',
    userTier = 'free',
    audioFile,
    audioUrl,
    styleId = 'cinematic-stock',
    durationSeconds = 60,
    bpm = 120,
    mood = '',
    genre = '',
    videoTitle = '',
    prompt = '',
    lyricsText = '',
  } = params;

  const report = (stage, percent, detail = '') => {
    onProgress?.({ stage, percent, detail });
  };

  // ── Step 1: Check daily limit ──
  report('Checking generation limits', 2);
  const limitCheck = await checkGenerationAllowed(userId, userTier, userEmail);
  if (!limitCheck?.allowed) {
    throw new Error(`Daily limit reached: ${limitCheck.reason}. Free plan allows ${limitCheck.limit} video per day.`);
  }

  // ── Step 2: Check coin balance ──
  report('Verifying coin balance', 5);
  const coinCheck = await checkCoinBalance(userId, 'full_music_video');
  if (!coinCheck?.sufficient && !isPlatformOwnerEmail(userEmail)) {
    throw new Error(`Insufficient coins. You need ${coinCheck.required} coins but have ${coinCheck.balance}. Purchase more coins on the Pricing page.`);
  }

  // ── Step 3: Scene segmentation ──
  report('Segmenting video into scenes', 7);
  const { sceneCount, sceneDurations, keyframesRequired } = segmentVideoIntoScenes(durationSeconds, bpm);
  report('Scene segmentation complete', 9, `${sceneCount} scenes, ${keyframesRequired} keyframes required`);

  // ── Step 4: Queue job with keyframe metadata ──
  report('Queuing generation job', 10);
  const job = await queueGenerationJob({
    userId,
    jobType: 'full_music_video',
    prompt,
    styleId,
    durationSeconds,
    bpm,
    mood,
    genre,
    videoTitle,
    audioUrl,
    keyframeCount: keyframesRequired,
  });

  // ── Step 5: Deduct coins ──
  if (!isPlatformOwnerEmail(userEmail)) {
    report('Deducting coins', 12);
    const deductResult = await deductCoinsForGeneration(userId, 'full_music_video', job?.id);
    if (!deductResult?.success) {
      await updateJobProgress(job?.id, { status: 'failed', error_message: 'Coin deduction failed' });
      throw new Error(deductResult?.error || 'Failed to deduct coins');
    }
  }

  await updateJobProgress(job?.id, { status: 'processing', progress_percent: 12, current_stage: 'analyzing_music' });

  // ── Step 6: Analyze music → generate scene prompts (Gemini Flash) ──
  report('Analyzing music with Gemini Flash', 20);
  const { scenes: scenePrompts, sceneCount: promptCount } = await analyzeMusicForScenes({
    title: videoTitle,
    genre,
    mood,
    prompt,
    duration: durationSeconds,
    bpm,
    styleId,
  });

  await updateJobProgress(job?.id, {
    scene_prompts: scenePrompts,
    progress_percent: 25,
    current_stage: 'generating_keyframes',
  });

  // ── Step 7: Generate keyframes — ONE AI image per scene (max 8) ──
  report(`Generating ${scenePrompts?.length} keyframes via Stable Diffusion`, 30);
  const keyframes = await generateKeyframes(
    scenePrompts,
    styleId,
    (done, total) => {
      const pct = 30 + Math.round((done / total) * 30); // 30–60%
      report(`Generating keyframe ${done}/${total}`, pct, `${done} of ${total} keyframes complete`);
    }
  );

  const imageUrls = keyframes?.map((kf) => kf?.imageUrl)?.filter(Boolean);

  await updateJobProgress(job?.id, {
    scene_images: keyframes,
    progress_percent: 60,
    current_stage: 'assigning_motion_transforms',
  });

  // ── Step 8: Assign motion transforms (pan/zoom/depth per keyframe) ──
  report('Assigning motion transforms', 62);
  const motionTransforms = assignMotionTransforms(keyframes?.length, mood);

  // ── Step 9: Build keyframe scene manifest with interpolation config ──
  report('Building keyframe scene manifest', 65);
  const keyframeManifest = buildKeyframeSceneManifest(keyframes, sceneDurations, mood);

  await updateJobProgress(job?.id, {
    animation_clips: keyframeManifest?.map((scene) => ({
      index: scene?.index,
      imageUrl: scene?.imageUrl,
      durationSeconds: scene?.durationSeconds,
      motionTransform: scene?.motionTransform,
      interpolation: scene?.interpolation,
      status: 'ready',
    })),
    progress_percent: 70,
    current_stage: 'frame_interpolation',
  });

  // ── Step 10: Frame interpolation — smooth motion between keyframes ──
  // No new AI frames are generated. The canvas engine applies motion transforms
  // and interpolates frames between keyframes at target FPS.
  report('Applying frame interpolation', 75);
  await new Promise((r) => setTimeout(r, 300)); // Simulate interpolation setup

  await updateJobProgress(job?.id, {
    progress_percent: 80,
    current_stage: 'assembling_video',
  });

  // ── Step 11: Assemble final video from keyframes + interpolation ──
  report('Assembling final video from keyframes', 85);
  await new Promise((r) => setTimeout(r, 300));

  // ── Step 12: Apply watermark for free tier ──
  const watermarked = shouldWatermark(userTier, userEmail);

  // ── Step 13: Attach audio ──
  report('Attaching audio track', 92);
  await new Promise((r) => setTimeout(r, 200));

  // ── Step 14: Mark complete ──
  await updateJobProgress(job?.id, {
    status: 'completed',
    progress_percent: 100,
    current_stage: 'complete',
    watermarked,
    output_video_url: audioUrl || null,
    metadata: {
      keyframe_mode: true,
      keyframes_generated: keyframes?.length,
      max_keyframes: KEYFRAME_CONFIG?.max_keyframes_per_video,
      rendering_mode: 'keyframe_interpolation',
      scene_count: sceneCount,
      total_duration_seconds: durationSeconds,
    },
  });

  report('Generation complete', 100);

  return {
    jobId: job?.id,
    sceneImages: imageUrls,
    scenePrompts,
    keyframeManifest,
    keyframesGenerated: keyframes?.length,
    watermarked,
    audioUrl,
    renderingMode: 'keyframe_interpolation',
  };
}

// ─── COIN TRANSACTION HISTORY ────────────────────────────────────────────────

export async function getCoinTransactionHistory(userId, limit = 30) {
  const { data, error } = await supabase?.from('coin_transactions')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

  if (error) return [];
  return (data || [])?.map((t) => ({
    id: t?.id,
    action: t?.action,
    amount: t?.amount,
    balanceAfter: t?.balance_after,
    reason: t?.reason,
    jobId: t?.job_id,
    createdAt: t?.created_at,
  }));
}

// ─── MULTI-COMPUTE WALLET RE-EXPORTS ─────────────────────────────────────────
// These functions are re-exported from computeWallet.js for backward compat
// and unified access from a single import.

export {
  checkMusicQuota,
  deductMusicGeneration,
  refundMusicGeneration,
  checkGameQuota,
  deductGameGeneration,
  refundGameGeneration,
  getComputeWalletStatus,
  COMPUTE_PLANS,
};
